import { Metric, onCLS, onFCP, onFID, onLCP, onTTFB } from 'web-vitals/attribution';
import { isSupportWebp } from '@fpkg/stats-utils';
import { BasePlugin, BaseStats, mergePluginOption, EventIds, PerformanceEvent, BaseEvent } from '@fpkg/stats-core';
import { onLoad, getURL } from '../utils';

import { PerformanceOpt, capturePerformanceData, formateResourceInfo } from '../data-capture-funcs/performance';

/**
 * 页面初始化标题，路径数据
 */
const firstPageData = {
  $title: document.title,
  $url: getURL(),
  $url_path: window.location.pathname,
};

/**
 * 是否初始化页面
 * 为了使性能统计数据更合理，性能数据上报时判断url链接是否变化，有变化不上报
 */
const isFirstPage = (): boolean => {
  const url = getURL();
  // 兼容 vue-router hash模式，
  // vue-router hash模式初始化时，如果当前url不包含 ‘#’ 号，会自动在路径后面添加 ‘#’
  return firstPageData.$url === url || `${firstPageData.$url}#/` === url;
};

const reportkeys = {
  CLS: 'cls_time', // 累积布局偏移：测量视觉稳定性。为了提供良好的用户体验，页面的 CLS 应保持在 0.1. 或更少
  FID: 'fid_time', // 首次输入延迟：测量交互性。为了提供良好的用户体验，页面的 FID 应为100 毫秒或更短
  LCP: 'lcp_time', // 最大内容绘制：测量加载性能。为了提供良好的用户体验，LCP 应在页面首次开始加载后的2.5 秒内发生。
  Custom_LCP: 'custom_lcp_time', // 自定义最大内容绘制
  TTFB: 'ttfb_time',
  FCP: 'fcp_time',
  INP: '',
};

/**
 * 性能监控插件
 */
export default class PerformancePlugin implements BasePlugin {
  name = 'performance';

  stats?: BaseStats;

  option: PerformanceOpt = {
    max_duration: 5 * 60 * 1000, // 默认超过5分钟不上报
    whether_lcp_with_resource_info: false, // 是否开启 LCP 请求链路上报
    lcp_with_resource_info_threshold: 2100, // LCP 上报请求链路信息的阈值
  };

  constructor(option: PerformanceOpt | null | boolean) {
    mergePluginOption(this.option, option);
  }

  setup(stats: BaseStats) {
    this.stats = stats;

    onLoad(() => {
      const performanceInfo = capturePerformanceData(this.option);

      if ((isFirstPage() || this.option.is_local_page) && performanceInfo) {
        performanceInfo.support_webp = isSupportWebp();
        performanceInfo.user_agent = window.navigator.userAgent || '';

        // 使用 web-vitals获取ttfb、fcp指标，所以删除
        delete performanceInfo.ttfb_time;
        delete performanceInfo.fcp_time;

        this.handlerEvent(performanceInfo);
      }
    });

    // 获取web-vitals指标
    // @see https://github.com/GoogleChrome/web-vitals
    onCLS(this.handlerWebVitals.bind(this));
    onFID(this.handlerWebVitals.bind(this));
    onLCP(this.handlerWebVitals.bind(this));
    onFCP(this.handlerWebVitals.bind(this));
    onTTFB(this.handlerWebVitals.bind(this));
  }

  handlerEvent(event: PerformanceEvent, microApp?: string) {
    if (this.stats) {
      const reportData: PerformanceEvent = {
        ...event,
      };
      if (microApp) {
        reportData.micro_app = microApp;
      }
      this.stats.event(reportData);
      this.stats.$bus.$emit('ON_PERFORMANCE_EVENT', reportData);
    }
  }

  handlerWebVitals(metric: Metric) {
    if (!metric || !reportkeys[metric.name] || !isFirstPage()) return;

    // TTFB也会存在的时间很长的情况，所以过滤掉
    if (['LCP', 'FCP', 'TTFB'].indexOf(metric.name) > -1 && metric.value > this.option.max_duration!) {
      // 过滤lcp不合法的情况
      return;
    }

    const eventParams: BaseEvent & { performance_time: number; [key: string]: string | number } = {
      event: EventIds.WebPerformance,
      [reportkeys[metric.name]]: Math.round(metric.value),
      performance_time: Math.round(performance.now()),
    };
    const LCPThreshold = this.option.lcp_with_resource_info_threshold || 2100;

    switch (metric.name) {
      case 'CLS':
        // @ts-ignore
        eventParams.debug_target = metric.attribution.largestShiftTarget || '';
        break;
      case 'FID':
        // @ts-ignore
        eventParams.debug_target = metric.attribution.eventTarget || '';
        break;
      case 'LCP':
        // @ts-ignore
        eventParams.debug_target = metric.attribution.element || '';
        if (this.option.whether_lcp_with_resource_info && eventParams.performance_time > Math.max(LCPThreshold, 2100)) {
          formateResourceInfo().forEach((item, index) => {
            this.stats &&
              this.stats.event({
                event: EventIds.FunctionEvent,
                event_code: 'LCPPerformanceResourceInfo',
                value: item.value,
                name: item.name,
                num: index,
              });
          });
        }
        break;
      default:
    }

    this.handlerEvent(eventParams);
  }
}
